// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — Mark.js   ·   THE MARK, the surface
// ────────────────────────────────────────────────────────────────────
// One login. One identity. One card, carried into every title.
//
// THE GLANCE is the whole design brief: you click a player in the Plaza,
// on the Pier, in a casino, in a listening room, and you get an ID —
// portrait, name, title, Mark code, Standing, Crests, and a hint of what
// they have for sale. Two seconds, then you carry on. It is only when you
// SNOOP that it opens into deeds, records, wardrobe and storefront.
//
// The Codex still owns the deep detail of any ONE game. The Mark owns
// the person, and the person is Empire-wide.
//
// ── VISUAL LANGUAGE ──────────────────────────────────────────────────
// This is the ILLEGALEMPIRE.SITE palette and typography, not the Grimoire
// manuscript palette. Deliberate: the Mark is the Empire's chrome, the
// one surface that must look identical whether it opens over Terror's
// starfield, Pierre's vellum, or Lyraea's player. Obsidian slabs, hairline
// edges, Bodoni display, mono micro-labels, one moving sheen, corner cuts.
// A game's own theme never leaks into it — that is what makes it portable.
//
// ── SAFETY ───────────────────────────────────────────────────────────
//   • READ-ONLY, with ONE exception (2026-10-25): the ADD FRIEND button on
//     somebody else's card. It owns no write path — it calls addByUid,
//     published on the social bridge by the friends dock (IEFriends.js ->
//     ieSocial.js), which is the same mutation the dock's own Add box runs.
//     No dock mounted in a title = no bridge = no button. Editing lives in
//     MarkEditor.js and is owner-gated there and in markData.js.
//   • Any uid works — yours or anyone's — because profiles are the shared
//     soul and authed-read by design.
//   • Pins are resolved against the REAL earned map. A pinned deed the
//     player does not hold renders as nothing at all, never as a badge.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   <Mark uid={myUid} viewingSelf onClose={close} />           // your own
//   <Mark uid={targetUid} onClose={close} />                   // snooping
//   <MarkChip uid={targetUid} onOpen={() => open(targetUid)} /> // nameplate
// ════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  useMark, deriveStanding, accentOf, markCode, ilkOf, partnerOf,
  fmtNum, fmtDate, fmtSince, MAX_LEVEL,
} from "./markData";
import {
  MK, mkCut, Kicker, Rule, Sheen, TopRule, injectMarkChrome, Markline,
  ownedVariantIds, setWornVariant,
} from "./markData";
import { useAchievements } from "./useAchievements";
import { useSocialBridge } from "./ieSocial";
import { getAchievementDisplay, GAME_NAMESPACES } from "./achievements";
import { PLAYER_SPRITES } from "./SpriteManifest";
// THE PLATE. The Mark is WHO the player is; the variant is only what
// that is printed on. One renderer for the foil, shared with the shop
// tile — THE ONE FACE COROLLARY: the shop never paints its own version
// of a plate, so "in the shop" and "on your Mark" cannot drift.
//
// ⚠ THE COHERENCY LAW (§2.22 Law 36). markVariants.js and MarkFoil.js
// are VENDORED — byte-identical in every title that carries The Mark,
// trunk at gamemaster/engine/. A plate must look the same here as it
// does in Pierre, in Terror and in Annihilation, because it is one
// identity and not four. Do not hand-edit a copy: edit the trunk and
// propagate. PLATES ARE WORN EVERYWHERE AND SOLD ONLY AT PIERRE — no
// title but Pierre may import a shop or a price from anywhere.
import { wornVariant, nextPlate, plateWheel, variantById } from "./markVariants";
import { PlateFace, FACE_TEXT, ReaderPanel, plateStyle } from "./MarkFoil";
import AchievementBadgeBook from "./AchievementBadgeBook";

// ── THE PALETTE — lifted from the Illegal Empire site, one place ─────
// MK now lives in markData.js so titles without the full achievement
// stack can still render a player in the Mark's chrome. Re-exported
// here so every existing `import { MK } from "./Mark"` keeps working.
export { MK, MK_CUT } from "./markData";

// Fonts, keyframes, Kicker, Rule and Sheen all live in markData.js now,
// so surfaces that cannot carry this file's achievement stack still wear
// the same chrome. See markData.js > THE CHROME ATOMS.

const Stat = ({ label, value, tint = MK.bone }) => (
  <div style={{ minWidth: 0 }}>
    <Kicker size={8}>{label}</Kicker>
    <div style={{
      fontFamily: MK.display, fontWeight: 600, fontSize: 19, lineHeight: 1.15,
      color: tint, marginTop: 3, whiteSpace: "nowrap",
      overflow: "hidden", textOverflow: "ellipsis",
    }}>{value}</div>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// THE PORTRAIT — sprite in a cut-corner obsidian plate
// ════════════════════════════════════════════════════════════════════
function Portrait({ gender, accent, seal, size = 104 }) {
  const src =
    PLAYER_SPRITES[`player_${gender}_front_a`] ||
    PLAYER_SPRITES[`player_${gender}_default`] ||
    PLAYER_SPRITES[`player_${gender}_idle_a`];
  return (
    <div style={{
      position: "relative", width: size, height: size, flex: "0 0 auto",
      background: `linear-gradient(155deg, ${MK.slabHi}, ${MK.slab} 45%, ${MK.obsidian})`,
      border: `1px solid ${MK.edge}`,
      boxShadow: `inset 0 0 40px ${accent.hex}14, 0 0 22px ${accent.hex}18`,
      clipPath: mkCut(12),
      display: "grid", placeItems: "center", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0, opacity: .5,
        background: `radial-gradient(ellipse at 50% 120%, ${accent.hex}30, transparent 62%)`,
      }} />
      {src ? (
        <img src={src} alt="" style={{
          width: "78%", height: "78%", objectFit: "contain",
          imageRendering: "pixelated", position: "relative",
          filter: `drop-shadow(0 3px 10px ${accent.hex}55)`,
        }} />
      ) : (
        <div style={{ fontFamily: MK.display, fontSize: size * .4, color: accent.hi }}>{seal}</div>
      )}
      <div style={{
        position: "absolute", right: 4, bottom: 2,
        fontFamily: MK.display, fontSize: 13, color: accent.hi, opacity: .8,
      }}>{seal}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STANDING BAR — three levels, never four
// ════════════════════════════════════════════════════════════════════
function StandingBar({ standing, accent }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <Kicker size={8.5} tint={accent.hi}>
          STANDING · {"I".repeat(Math.max(1, standing.level)) || "—"}{standing.level === 0 ? "" : ""} · {standing.name}
        </Kicker>
        <div style={{ fontFamily: MK.mono, fontSize: 9, color: MK.smokeP, whiteSpace: "nowrap" }}>
          {standing.maxed ? "CEILING" : `${standing.ranksIntoLevel}/${standing.ranksPerLevel} RANKS`}
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
        {Array.from({ length: MAX_LEVEL }).map((_, i) => {
          const filled = i < standing.level;
          const active = i === standing.level && !standing.maxed;
          return (
            <div key={i} style={{
              flex: 1, height: 4, background: MK.obsidian,
              border: `1px solid ${filled || active ? accent.hex + "66" : MK.edge}`,
              position: "relative", overflow: "hidden",
            }}>
              <div style={{
                position: "absolute", inset: 0,
                width: filled ? "100%" : active ? `${Math.round(standing.pct * 100)}%` : 0,
                background: `linear-gradient(90deg, ${accent.hex}, ${accent.hi})`,
                boxShadow: `0 0 10px ${accent.hex}88`,
                transition: "width .5s cubic-bezier(.22,.61,.36,1)",
              }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PINNED DEEDS — resolved against what was actually earned
// ════════════════════════════════════════════════════════════════════
function PinRail({ pinned, earnedSet, map, accent, onOpenDeeds }) {
  const real = (pinned || []).filter((id) => earnedSet.has(id)).slice(0, 6);
  if (!real.length) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {real.map((id) => {
        const d = getAchievementDisplay(id, true) || {};
        const tint = d.tint || accent.hex;
        return (
          <div key={id} title={d.name || id} onClick={onOpenDeeds} style={{
            display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
            padding: "4px 8px", background: MK.obsidian,
            border: `1px solid ${tint}44`, boxShadow: `0 0 12px ${tint}18`,
          }}>
            <span style={{ fontFamily: MK.display, fontSize: 12, color: tint }}>
              {d.glyph || d.icon || "◈"}
            </span>
            <span style={{
              fontFamily: MK.sans, fontSize: 11.5, letterSpacing: ".04em",
              color: MK.bone, maxWidth: 132, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{d.name || id}</span>
          </div>
        );
      })}
      <div style={{ fontFamily: MK.mono, fontSize: 9, color: MK.smokeP, alignSelf: "center", letterSpacing: ".2em" }}>
        {map ? `${earnedSet.size} TOTAL` : ""}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STOREFRONT — the shop window. Decorative by design; see markData.js.
// ════════════════════════════════════════════════════════════════════
function StoreRail({ items, accent, compact }) {
  if (!items?.length) return (
    <div style={{ fontFamily: MK.sans, fontSize: 13, color: MK.smokeP, padding: "10px 0" }}>
      Nothing for sale.
    </div>
  );
  const shown = compact ? items.slice(0, 3) : items;
  return (
    <div style={{
      display: "grid", gap: 6,
      gridTemplateColumns: compact ? "1fr" : "repeat(auto-fill, minmax(190px, 1fr))",
    }}>
      {shown.map((it) => (
        <div key={it.id} style={{
          display: "flex", alignItems: "center", gap: 9, padding: 7,
          background: `linear-gradient(155deg, ${MK.slabHi}, ${MK.slab})`,
          border: `1px solid ${MK.edge}`,
        }}>
          <div style={{
            width: 34, height: 34, flex: "0 0 auto", background: MK.obsidian,
            border: `1px solid ${MK.edge}`, display: "grid", placeItems: "center",
            backgroundImage: it.cover ? `url(${it.cover})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
          }}>
            {!it.cover && (
              <span style={{ fontFamily: MK.mono, fontSize: 8, color: MK.smokeP }}>
                {String(it.kind || "•").slice(0, 3).toUpperCase()}
              </span>
            )}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontFamily: MK.sans, fontSize: 13.5, color: MK.bone, letterSpacing: ".02em",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{it.title}</div>
            <div style={{ fontFamily: MK.mono, fontSize: 8.5, letterSpacing: ".2em", color: MK.smokeP, textTransform: "uppercase" }}>
              {it.kind}
            </div>
          </div>
          <div style={{
            fontFamily: MK.mono, fontSize: 11, color: it.priceDC ? accent.hi : MK.verdP,
            whiteSpace: "nowrap", paddingLeft: 4,
          }}>
            {it.priceDC ? `◆ ${fmtNum(it.priceDC)}` : "FREE"}
          </div>
        </div>
      ))}
      {compact && items.length > 3 && (
        <div style={{ fontFamily: MK.mono, fontSize: 9, letterSpacing: ".2em", color: MK.smokeP }}>
          +{items.length - 3} MORE
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MARK CHIP — the nameplate. What you actually click in a Plaza.
// ════════════════════════════════════════════════════════════════════
export function MarkChip({ uid, onOpen, dense = false }) {
  injectMarkChrome();
  const m = useMark(uid);
  const accent = accentOf(m.mark);
  const variant = wornVariant(m.mark);
  return (
    // ⭐ A NAMEPLATE IS A PLACE THE PLATE IS SHOWN, so it is a face like
    // every other. Display text only, all of it bone with FACE_TEXT —
    // an accent-coloured name over a bright foil is unreadable for five
    // of the seven accents, and shipping that would be its own small
    // catfish. (THE NO-CATFISH LAW, MarkFoil.js.)
    <PlateFace
      variant={variant}
      cut={7}
      style={{
        display: "inline-block", cursor: "pointer",
        boxShadow: `0 0 14px ${accent.hex}1a`,
      }}
    >
      <div onClick={onOpen} title={`${m.name} · ${m.code}`}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: dense ? "3px 7px" : "5px 10px",
      }}>
      <span style={{ fontFamily: MK.display, fontSize: 12, color: MK.bone, ...FACE_TEXT }}>{m.seal}</span>
      <span style={{
        fontFamily: MK.sans, fontSize: dense ? 12.5 : 14, color: MK.bone,
        letterSpacing: ".03em", maxWidth: 140, overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap", ...FACE_TEXT,
      }}>{m.name}</span>
      {m.storeOpen && (
        <span style={{
          fontFamily: MK.mono, fontSize: 8, letterSpacing: ".18em",
          color: "#06060a", background: MK.verdP, padding: "2px 5px",
        }}>SHOP</span>
      )}
      </div>
      </div>
    </PlateFace>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE MARK
// ════════════════════════════════════════════════════════════════════
export default function Mark({ uid, viewingSelf = false, onClose, onEdit, embedded = false }) {
  injectMarkChrome();
  const m = useMark(uid);
  const { map, earnedIds, renown } = useAchievements(uid);
  const [tab, setTab] = useState(null);   // null = THE GLANCE

  const accent = accentOf(m.mark);
  // Unknown/retired ids self-heal to the Classic inside wornVariant —
  // retiring a plate must never blank somebody's ID card.
  const variant = wornVariant(m.mark);
  const ilk = ilkOf(m.mark);   // H-? / A-? under the name

  // ── ⭐ THE WHEEL. Click THE MARK to cycle your plates. ─────────────
  // caP'n: "changing marks is a bit tedious." It was — a plate lived
  // three clicks and a walk away, inside a shop, which is the right
  // place to BUY one and the wrong place to try one on.
  //
  // Owned plates are read off the profile the Mark already holds, so
  // this costs no second listener. Equipping is not selling: any title
  // may re-plate its own player (cosmetics are equip-anywhere), and
  // only Pierre may vend. (COHERENCY LAW, §2.22 Law 36.)
  const owned = useMemo(() => ownedVariantIds(m.profile), [m.profile]);
  const wheel = useMemo(() => plateWheel(owned), [owned]);
  const canCycle = viewingSelf && wheel.length > 1;
  const [flip, setFlip] = useState(null);   // the name of the plate just put on
  const cyclePlate = () => {
    if (!canCycle) return;
    const next = nextPlate(owned, variant.id);
    if (!next) return;
    setFlip(variantById(next).name);
    setTimeout(() => setFlip(null), 1400);
    // Fire and forget: the plate is already rendering from the live
    // listener by the time this resolves, and a failed write leaves the
    // old plate on rather than a half-changed card.
    setWornVariant(uid, next);
  };

  // ── ⭐ ADD FRIEND (2026-10-25). Click anyone's Mark -> add them. ─────
  // The relation is read off the lists the friends dock already holds, so
  // the button follows reality live: ADD FRIEND -> REQUEST SENT -> FRIENDS.
  // They asked me first ("in") -> the same click is the accept (addByUid does it).
  const social = useSocialBridge();
  const rel = useMemo(() => {
    if (viewingSelf || !uid || !social || !social.uid || !social.addByUid || social.uid === uid) return null;
    const has = (arr) => (arr || []).some((r) => r && r.uid === uid);
    if (has(social.friends))     return "friend";
    if (has(social.requestsOut)) return "out";
    if (has(social.requestsIn))  return "in";
    if (has(social.blocked))     return null;       // never nudge toward someone they blocked
    return "none";
  }, [social, uid, viewingSelf]);
  const [adding, setAdding]   = useState(false);
  const [addNote, setAddNote] = useState(null);
  const addFriend = async () => {
    if (adding || !social || !social.addByUid) return;
    setAdding(true); setAddNote(null);
    let r;
    try { r = await social.addByUid(uid, m.name); } catch { r = { ok: false, error: "Could not send that." }; }
    setAdding(false);
    if (!r || !r.ok) setAddNote((r && r.error) || "Could not send that.");
  };

  const standing = useMemo(() => deriveStanding(renown), [renown]);
  const earnedSet = useMemo(() => new Set(earnedIds || []), [earnedIds]);

  // Esc closes — the glance is meant to be dismissed without aiming.
  //
  // ⚠ CAPTURE PHASE, AND IT SWALLOWS. The Mark is now opened from the
  // friends panel, which is mounted as a SIBLING of <App/> — so every
  // window-level key handler the game has is still live underneath it.
  // Terror alone has Escape handlers in Plaza (four), StartMenu,
  // Dungeon1, ChatSystem, DialogueEngine, FactionHall, NpcStage,
  // StoryCutscene, WilliesWorkshop and KeyboardNav. Without capture +
  // stopPropagation, one Escape over a friend's Mark closed the Mark
  // AND cancelled the mode-select behind it, and the arrow keys kept
  // walking the player around a room they could not see.
  useEffect(() => {
    if (embedded) return;
    const SWALLOW = new Set([
      "Escape", "Enter", " ", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
      "w", "a", "s", "d", "W", "A", "S", "D",
    ]);
    const h = (e) => {
      if (!SWALLOW.has(e.key)) return;
      e.stopPropagation();
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (tab) setTab(null); else onClose?.();
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [tab, onClose, embedded]);

  if (m.loading) {
    return (
      <Shell embedded={embedded} onClose={onClose} accent={accent} variant={variant}>
        <div style={{ padding: 40, textAlign: "center", fontFamily: MK.mono, fontSize: 10, letterSpacing: ".3em", color: MK.smokeP, animation: "mkPulse 1.4s infinite" }}>
          READING THE MARK
        </div>
      </Shell>
    );
  }

  if (!m.profile) {
    return (
      <Shell embedded={embedded} onClose={onClose} accent={accent} variant={variant}>
        <div style={{ padding: 40, textAlign: "center" }}>
          <Kicker>NO MARK</Kicker>
          <div style={{ fontFamily: MK.display, fontSize: 22, color: MK.bone, marginTop: 8 }}>Unclaimed</div>
          <div style={{ fontFamily: MK.sans, fontSize: 13, color: MK.ashP, marginTop: 6 }}>
            This code answers to nobody. {markCode(uid)}
          </div>
        </div>
      </Shell>
    );
  }

  const TABS = [
    ["deeds",   "DEEDS"],
    ["records", "RECORDS"],
    ["store",   "STORE"],
    ["about",   "ABOUT"],
  ];

  const body = (
    <Shell embedded={embedded} onClose={onClose} accent={accent} variant={variant}>
      {/* ── ⭐ THE FACE ────────────────────────────────────────────
          The product shot. The SAME component the shop tile mounts, at
          full foil strength, so the plate you bought is the plate you
          are wearing — pixel for pixel. Display text only: every word
          in here wears FACE_TEXT, and body copy lives on the slab
          below. (THE NO-CATFISH LAW, MarkFoil.js.) */}
      <div style={{ position: "relative", padding: "16px 18px 15px", overflow: "hidden" }}>
        <Sheen tint={accent.hi} />

        <div style={{ display: "flex", gap: 14, position: "relative", alignItems: "flex-start" }}>
          <Portrait gender={m.gender} accent={accent} seal={m.seal} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* THE MARK, and on your own card it is also the switch.
                  A label that does something has to LOOK like it does
                  something — hence the cursor, the count and the hover
                  rule; a secret control is not a feature. */}
              <div
                onClick={cyclePlate}
                title={canCycle
                  ? `Click to change plate — ${wheel.length} owned`
                  : undefined}
                className={canCycle ? "mk-tab" : undefined}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  cursor: canCycle ? "pointer" : "default",
                  userSelect: "none",
                }}
              >
                <span style={{
                  fontFamily: MK.mono, fontSize: 9, letterSpacing: ".28em",
                  textTransform: "uppercase", color: MK.bone, ...FACE_TEXT,
                }}>THE MARK</span>
                {canCycle && (
                  <span style={{
                    fontFamily: MK.mono, fontSize: 8, letterSpacing: ".16em",
                    color: "#06060a", background: MK.bone,
                    padding: "2px 6px", whiteSpace: "nowrap",
                  }}>⟳ {wheel.length}</span>
                )}
                {flip && (
                  <span style={{
                    fontFamily: MK.display, fontStyle: "italic", fontSize: 12,
                    color: MK.bone, animation: "mkFade .25s both",
                    whiteSpace: "nowrap", ...FACE_TEXT,
                  }}>{flip}</span>
                )}
              </div>
              <span style={{
                fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".2em",
                color: MK.bone, opacity: .82, ...FACE_TEXT,
              }}>{m.code}</span>
              {m.faction && (
                <span style={{
                  fontFamily: MK.mono, fontSize: 8, letterSpacing: ".2em",
                  color: "#06060a", background: MK.bone, padding: "2px 6px",
                  textTransform: "uppercase",
                }}>{m.faction}</span>
              )}
            </div>

            <div style={{
              fontFamily: MK.display, fontWeight: 800, fontSize: 30, lineHeight: 1.05,
              color: MK.bone, marginTop: 4, letterSpacing: ".01em",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              ...FACE_TEXT,
            }}>{m.name}</div>

            {/* ⭐ THE ILK (caP'n 2026-09-26) — right under the name. H for a
                person, A for their AI, and a tag they pick (or keep the ?).
                Subtle on purpose: the Mark says it, it doesn't shout it. */}
            <div title={`${ilk.name} · ${ilk.label}`} style={{
              fontFamily: MK.mono, fontSize: 10, letterSpacing: ".24em",
              color: MK.bone, opacity: .78, marginTop: 3, ...FACE_TEXT,
            }}>{ilk.label}</div>

            {m.title && (
              <div style={{
                fontFamily: MK.display, fontStyle: "italic", fontSize: 14,
                color: MK.bone, opacity: .92, marginTop: 1, ...FACE_TEXT,
              }}>{m.title}</div>
            )}

            {rel && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={rel === "none" || rel === "in" ? addFriend : undefined}
                  disabled={adding || rel === "friend" || rel === "out"}
                  title={rel === "none" ? "Send a friend request" : rel === "in" ? "They asked you first — accept" : undefined}
                  style={{
                    fontFamily: MK.mono, fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase",
                    padding: "5px 10px", border: "none", whiteSpace: "nowrap",
                    cursor: (rel === "none" || rel === "in") && !adding ? "pointer" : "default",
                    color: (rel === "none" || rel === "in") ? "#06060a" : MK.bone,
                    background: (rel === "none" || rel === "in") ? MK.bone : "transparent",
                    outline: (rel === "none" || rel === "in") ? "none" : `1px solid ${MK.bone}55`,
                    opacity: adding ? .6 : 1,
                  }}
                >{adding ? "…" : rel === "friend" ? "FRIENDS ✓" : rel === "out" ? "REQUEST SENT" : rel === "in" ? "ACCEPT FRIEND" : "ADD FRIEND"}</button>
                {addNote && (
                  <span style={{ fontFamily: MK.sans, fontSize: 12, color: MK.bone, ...FACE_TEXT }}>{addNote}</span>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── ⭐ THE READER PANEL. The plate keeps running underneath at
             full strength — this is a data block stamped INTO it, not a
             dimmer laid over it. Everything the player has to read
             lives inside, and about a quarter of the plate still shows
             straight through. ── */}
      <ReaderPanel variant={variant} cut={14} style={{ margin: "0 8px 8px" }}>
      <div style={{ position: "relative", padding: "13px 14px 14px" }}>
        {m.mark.tagline ? (
          <div style={{ fontFamily: MK.sans, fontSize: 14, color: MK.ashP, lineHeight: 1.35 }}>
            {m.mark.tagline}
          </div>
        ) : null}

        {m.mark.status ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 7 }}>
            <span style={{ width: 5, height: 5, background: MK.verdP, borderRadius: "50%", boxShadow: `0 0 8px ${MK.verdP}` }} />
            <span style={{ fontFamily: MK.mono, fontSize: 10, color: MK.verdP, letterSpacing: ".08em" }}>
              {m.mark.status}
            </span>
          </div>
        ) : null}

        <div style={{ marginTop: 14, position: "relative" }}>
          <StandingBar standing={standing} accent={accent} />
        </div>

        {/* ⭐ THE MARKLINE. Terror's barcode, brought up a level and
            made the PERSON's. Seeded from the Mark Code, so it is the
            same strip for the same player in every game in the Empire,
            forever — and it cannot be forged, because the code it is
            drawn from is derived and stored nowhere.
            It is decorative and it READS AS PROCESSING, which is the
            entire point of a mark the Empire puts on you whether you
            like it or not. */}
        <div style={{ marginTop: 13, position: "relative", display: "flex", alignItems: "center", gap: 9 }}>
          <Markline seed={m.code} name={m.name} mode="code"
                    height={20} tint={accent.hi} tintHi={accent.hi} opacity={0.9} />
          <div style={{
            flexShrink: 0, fontFamily: MK.mono, fontSize: 9,
            letterSpacing: ".22em", color: MK.ashP, whiteSpace: "nowrap",
          }}>{m.code}</div>
        </div>
      </div>

      <Rule tint={accent.hex + "55"} />

      {/* ── THE GLANCE ──────────────────────────────────────────── */}
      {!tab && (
        <div style={{ padding: "14px 18px 16px", animation: "mkFade .25s both" }}>
          <div style={{
            display: "grid", gap: 12, marginBottom: 14,
            gridTemplateColumns: "repeat(auto-fit, minmax(78px, 1fr))",
          }}>
            <Stat label="CRESTS"  value={`◆ ${fmtNum(m.crests)}`} tint={accent.hi} />
            <Stat label="DEEDS"   value={fmtNum(earnedSet.size)} />
            <Stat label="RENOWN"  value={fmtNum(renown)} />
            <Stat label="TITLES"  value={fmtNum(m.gamesTouched)} />
            <Stat label="MARKED"  value={fmtSince(m.since)} />
          </div>

          <PinRail
            pinned={m.mark.pinned}
            earnedSet={earnedSet}
            map={map}
            accent={accent}
            onOpenDeeds={() => setTab("deeds")}
          />

          {m.storeOpen && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <Kicker tint={MK.verdP}>FOR SALE</Kicker>
                <div onClick={() => setTab("store")} className="mk-tab" style={{
                  fontFamily: MK.mono, fontSize: 9, letterSpacing: ".2em",
                  color: MK.smokeP, cursor: "pointer",
                }}>SEE ALL →</div>
              </div>
              <StoreRail items={m.storeItems} accent={accent} compact />
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <Rule />
            <div style={{ display: "flex", gap: 16, paddingTop: 11, flexWrap: "wrap" }}>
              {TABS.map(([k, label]) => (
                <div key={k} onClick={() => setTab(k)} className="mk-tab" style={{
                  fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".26em",
                  color: MK.smokeP, cursor: "pointer", transition: "color .25s",
                }}>{label}</div>
              ))}
              {viewingSelf && onEdit && (
                <div onClick={onEdit} className="mk-tab" style={{
                  fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".26em",
                  color: accent.hi, cursor: "pointer", marginLeft: "auto",
                }}>✎ EDIT</div>
              )}
            </div>
            <div style={{ fontFamily: MK.sans, fontSize: 12, color: MK.smokeP, marginTop: 8, fontStyle: "italic" }}>
              {viewingSelf ? "Your Mark. Snoop your own record." : "Snoop around — there is more under the surface."}
            </div>
          </div>
        </div>
      )}

      {/* ── THE DEEP ────────────────────────────────────────────── */}
      {tab && (
        <div style={{ animation: "mkFade .2s both" }}>
          <div style={{
            display: "flex", gap: 14, padding: "9px 18px",
            borderBottom: `1px solid ${MK.edge}`, flexWrap: "wrap", alignItems: "center",
          }}>
            <div onClick={() => setTab(null)} className="mk-tab" style={{
              fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".26em",
              color: accent.hi, cursor: "pointer",
            }}>← ID</div>
            {TABS.map(([k, label]) => (
              <div key={k} onClick={() => setTab(k)} className="mk-tab" style={{
                fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".26em", cursor: "pointer",
                color: tab === k ? MK.bone : MK.smokeP,
                borderBottom: tab === k ? `1px solid ${accent.hex}` : "1px solid transparent",
                paddingBottom: 2, transition: "color .25s",
              }}>{label}</div>
            ))}
          </div>

          <div className="mk-scroll" style={{ padding: "14px 18px 18px", maxHeight: "52vh", overflowY: "auto" }}>
            {/* DEEDS is not rendered here — the badge book is its own
                full-screen surface (fixed, z-index 800) and would land
                BEHIND this card if nested. It is raised above the Mark
                at the bottom of this component instead. */}
            {tab === "records" && <Records m={m} accent={accent} renown={renown} deeds={earnedSet.size} />}

            {tab === "store" && (
              <>
                <Kicker tint={MK.verdP}>{m.storeOpen ? "FOR SALE" : "SHOP CLOSED"}</Kicker>
                <div style={{ height: 8 }} />
                <StoreRail items={m.storeItems} accent={accent} />
              </>
            )}

            {tab === "about" && <About m={m} uid={uid} accent={accent} />}
          </div>
        </div>
      )}
      </ReaderPanel>
    </Shell>
  );

  // THE BADGE BOOK — raised above the Mark, and deliberately OUTSIDE the
  // Shell. Two reasons, both bugs if ignored: its own overlay is fixed at
  // z-index 800 (it would render behind the Mark's 9000), and the Shell's
  // card carries a clipPath — a clipped ancestor clips even a position:fixed
  // descendant, which would slice the book to the card's silhouette.
  return (
    <>
      {body}
      {tab === "deeds" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9500 }}>
          <AchievementBadgeBook
            uid={uid}
            viewingSelf={viewingSelf}
            playerName={m.name}
            onClose={() => setTab(null)}
          />
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// RECORDS — lifetime numbers, per-title breakdown
// ════════════════════════════════════════════════════════════════════
function Records({ m, accent, renown, deeds }) {
  const games = Object.entries(m.gamesPlayed || {})
    .sort((a, b) => (b[1] || 0) - (a[1] || 0));
  return (
    <>
      <Kicker>LIFETIME</Kicker>
      <div style={{
        display: "grid", gap: 12, margin: "10px 0 18px",
        gridTemplateColumns: "repeat(auto-fit, minmax(92px, 1fr))",
      }}>
        <Stat label="SESSIONS"   value={fmtNum(m.totalSessions)} />
        <Stat label="DEEDS"      value={fmtNum(deeds)} />
        <Stat label="RENOWN"     value={fmtNum(renown)} />
        <Stat label="CRESTS"     value={`◆ ${fmtNum(m.crests)}`} tint={accent.hi} />
        <Stat label="CARDS"      value={fmtNum(m.collectionSize)} />
        <Stat label="COSMETICS"  value={fmtNum(m.cosmeticsOwned)} />
        <Stat label="FRIENDS"    value={fmtNum(m.friends)} />
      </div>

      <Rule />
      <div style={{ height: 12 }} />
      <Kicker>TITLES WALKED</Kicker>
      <div style={{ marginTop: 8, display: "grid", gap: 5 }}>
        {games.length === 0 && (
          <div style={{ fontFamily: MK.sans, fontSize: 13, color: MK.smokeP }}>No title has recorded them yet.</div>
        )}
        {games.map(([g, n]) => (
          <div key={g} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "6px 9px", background: MK.obsidian, border: `1px solid ${MK.edge}`,
          }}>
            <span style={{ fontFamily: MK.sans, fontSize: 14, color: MK.bone, letterSpacing: ".03em" }}>
              {GAME_NAMESPACES?.[g]?.name || GAME_NAMESPACES?.[g]?.label || g}
            </span>
            <span style={{ fontFamily: MK.mono, fontSize: 10, color: MK.ashP }}>{fmtNum(n)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ABOUT — the identity page. The dullest tab and the one that proves
// the Mark is a real ID rather than a decoration.
// ════════════════════════════════════════════════════════════════════
function About({ m, uid, accent }) {
  const links = Object.entries(m.mark.links || {});
  const ilk = ilkOf(m.mark);
  const partner = usePartnerLabel(m, uid);
  const row = (k, v) => (
    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: `1px solid ${MK.edge}` }}>
      <span style={{ fontFamily: MK.mono, fontSize: 9, letterSpacing: ".2em", color: MK.smokeP }}>{k}</span>
      <span style={{ fontFamily: MK.sans, fontSize: 13.5, color: MK.bone, textAlign: "right" }}>{v}</span>
    </div>
  );
  return (
    <>
      <Kicker tint={accent.hi}>IDENTITY</Kicker>
      <div style={{ marginTop: 8 }}>
        {row("MARK CODE", m.code)}
        {row("SEAL", m.seal)}
        {row("MARKED SINCE", fmtDate(m.since))}
        {row("COMPANION", m.companionId || "—")}
        {row("FACTION", m.faction || "unaligned")}
        {row("ILK", `${ilk.name} · ${ilk.label}`)}
        {row("PARTNER", partner)}
      </div>

      {links.length > 0 && (
        <>
          <div style={{ height: 16 }} />
          <Kicker>ELSEWHERE</Kicker>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {links.map(([label, url]) => (
              <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="mk-link" style={{
                fontFamily: MK.mono, fontSize: 10, letterSpacing: ".14em", textDecoration: "none",
                color: MK.ashP, border: `1px solid ${MK.edge}`, padding: "5px 9px", transition: "all .25s",
              }}>{label.toUpperCase()} ↗</a>
            ))}
          </div>
        </>
      )}

      <div style={{ height: 18 }} />
      <div style={{ fontFamily: MK.sans, fontSize: 12, color: MK.smokeP, lineHeight: 1.5, fontStyle: "italic" }}>
        One Mark, every title. Crests, deeds, wardrobe and records follow this
        code wherever it walks — nothing here is stored twice, so nothing here
        can disagree with itself.
      </div>
    </>
  );
}

// ⭐ PARTNER — the co-op partner this Mark has named (2026-09-26).
// One-sided by design; "⇄" only when the partner has named this Mark
// back. One extra profile listener, and only while About is open.
function usePartnerLabel(m, uid) {
  const pUid = partnerOf(m.mark);
  const p = useMark(pUid);
  if (!pUid) return "—";
  if (p.loading) return "…";
  if (!p.profile) return "unclaimed";
  const mutual = !!uid && partnerOf(p.mark) === uid;
  return mutual ? `${p.name}  ⇄` : p.name;
}

// ════════════════════════════════════════════════════════════════════
// SHELL — the slab, the cut corners, the backdrop
// ════════════════════════════════════════════════════════════════════
function Shell({ children, onClose, accent, embedded, variant }) {
  // ⭐ THE WHOLE CARD IS THE PLATE. caP'n, 2026-08-25: "the entire thing
  // should be the cool new mark we bought, not just the top header."
  //
  // So the card IS a PlateFace — the same renderer, at the same one
  // strength, as the shop tile. Nothing is dimmed anywhere. Legibility
  // comes from ReaderPanels stamped into the foil around each block of
  // text, which is chrome laid ON the item rather than a dimmer applied
  // TO it. (THE NO-CATFISH LAW, MarkFoil.js — read the distinction
  // there before touching either.)
  const card = (
    <PlateFace
      variant={variant}
      cut={18}
      style={{
        width: "min(560px, 96vw)",
        border: `1px solid ${(variant?.plate?.edge) || MK.edge}`,
        boxShadow: `0 26px 80px #000c, 0 0 44px ${accent.hex}18`,
        animation: "mkRise .3s cubic-bezier(.22,.61,.36,1) both",
      }}
    >
      <TopRule accent={accent} />
      {onClose && !embedded && (
        <div onClick={onClose} title="Close (Esc)" style={{
          position: "absolute", top: 10, right: 14, zIndex: 3, cursor: "pointer",
          fontFamily: MK.mono, fontSize: 12, color: MK.bone, padding: 4,
          ...FACE_TEXT,
        }}>✕</div>
      )}
      {children}
    </PlateFace>
  );

  if (embedded) return card;

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose?.()} style={{
      position: "fixed", inset: 0, zIndex: 9000,
      background: "rgba(3,3,6,.82)", backdropFilter: "blur(7px)",
      display: "grid", placeItems: "center", padding: 16,
      animation: "mkFade .2s both",
    }}>{card}</div>
  );
}
